import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  MessageSquare,
  RefreshCw,
  RotateCcw,
  ScanSearch,
  Search,
  Trash2,
  Volume2,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  PanelTableCard,
  PanelTableHeader,
  PanelTableFrame,
  PanelTableHeadRow,
  PanelTableHead,
  PanelTableBody,
  PanelTableRow,
  PanelTableCell,
  PanelTableEmpty,
  PanelTableFooter,
  PanelTablePagination,
  PanelTableActions,
  usePanelTablePagination,
} from "@/components/ui/panel-table";
import { PanelModal, PanelModalContent } from "@/components/ui/panel-modal";

const ERROR_TYPE_LABELS = {
  missing: "Dosya yok",
  corrupted: "Bozuk ses",
  wrong_file: "Yanlış ses",
};

const STATUS_LABELS = {
  pending: "Bekliyor",
  fixed: "Düzeltildi",
  not_found: "Bulunamadı",
};

const STATUS_FILTERS = [
  { id: "all", label: "Tümü" },
  { id: "pending", label: "Bekleyen" },
  { id: "fixed", label: "Düzeltildi" },
  { id: "not_found", label: "Bulunamadı" },
];

const TYPE_FILTERS = [
  { id: "all", label: "Tüm hatalar" },
  { id: "missing", label: "Dosya yok" },
  { id: "corrupted", label: "Bozuk ses" },
  { id: "wrong_file", label: "Yanlış ses" },
];

const getErrorTypeLabel = (type) => ERROR_TYPE_LABELS[type] || type;
const getErrorTypeClass = (type, isDark) => {
  if (type === "missing") return isDark ? "text-rose-300" : "text-rose-700";
  if (type === "corrupted") return isDark ? "text-amber-300" : "text-amber-700";
  return isDark ? "text-orange-300" : "text-orange-700";
};
const getReportStatus = (row) => row?.status || "pending";

const buildRecordFromReport = (row, cdrRecord = null) => ({
  id: row.cdr_id,
  cdr_id: row.cdr_id,
  arayan_numara: cdrRecord?.arayan_numara ?? row.arayan_numara,
  aranan_numara: cdrRecord?.aranan_numara ?? row.aranan_numara,
  tarih: cdrRecord?.tarih ?? row.call_tarih,
  agent_id: cdrRecord?.agentId ?? row.agent_id,
  agent_name: cdrRecord?.agent_name ?? row.agent_name,
  company_id: cdrRecord?.company_id ?? row.company_id,
  konusma_suresi: cdrRecord?.konusma_suresi ?? row.konusma_suresi ?? 0,
  calma_suresi: row.calma_suresi ?? 0,
  yon: cdrRecord?.yon ?? row.call_yon ?? row.yon,
});

const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return secs === 0 ? `${mins}dk` : `${mins}dk ${secs}s`;
};

const formatCallDirection = (yon) => {
  const value = Number(yon);
  if (value === 1) return "Gelen";
  if (value === 2) return "Giden";
  return "—";
};

const DetailField = ({ label, value, isDark, labelClass, textClass, mono = false }) => (
  <div>
    <p className={`text-[10px] uppercase tracking-wide ${labelClass}`}>{label}</p>
    <p className={`text-sm mt-0.5 ${mono ? "font-mono" : ""} ${textClass}`}>{value || "—"}</p>
  </div>
);

const RecordingErrorReportsSettings = ({ apiBase, isDark, textClass, labelClass, onPlayRecording, onOpenCdrRecord }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [detailRow, setDetailRow] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [recheckingId, setRecheckingId] = useState(null);
  const [recheckProgress, setRecheckProgress] = useState(null);

  const RECHECK_ITEM_TIMEOUT_MS = 55000;

  const inputClass = isDark
    ? "bg-slate-950/80 border-slate-700 text-white placeholder:text-slate-500"
    : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400";
  const cardClass = isDark ? "bg-slate-950/50 border-slate-800" : "bg-white border-gray-200 shadow-sm";
  const pillClass = (active) => (
    active
      ? "bg-slate-700 text-white border-slate-600"
      : isDark
        ? "border-slate-700 text-slate-300 hover:bg-slate-800"
        : "border-gray-200 text-gray-600 hover:bg-gray-50"
  );

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500, offset: 0 };
      if (search.trim()) params.search = search.trim();
      if (typeFilter !== "all") params.error_type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await axios.get(`${apiBase}/recording-error-reports`, { params });
      setItems(res.data.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Hata bildirimleri yüklenemedi");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, search, typeFilter, statusFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    setSelectedIds([]);
  }, [search, typeFilter, statusFilter]);

  const pagination = usePanelTablePagination(items, {
    pageSize: 20,
    resetDeps: [search, typeFilter, statusFilter, items.length],
  });

  const formatDate = (value, short = false) => {
    if (!value) return "—";
    try {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleString("tr-TR", short
        ? { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }
        : undefined);
    } catch {
      return value;
    }
  };

  const getStatusBadge = (status) => {
    if (status === "fixed") {
      return (
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isDark ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}>
          {STATUS_LABELS.fixed}
        </Badge>
      );
    }
    if (status === "not_found") {
      return (
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isDark ? "border-rose-500/40 text-rose-300 bg-rose-500/10" : "border-rose-300 text-rose-700 bg-rose-50"}`}>
          {STATUS_LABELS.not_found}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isDark ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
        {STATUS_LABELS.pending}
      </Badge>
    );
  };

  const handlePlayRecording = (row, cdrRecord = null) => {
    const url = cdrRecord?.recording || row.recording_url;
    if (!url) {
      toast.error("Bu bildirimde ses dosyası URL'si kayıtlı değil");
      return;
    }
    if (typeof onPlayRecording === "function") {
      onPlayRecording(url, buildRecordFromReport(row, cdrRecord));
    }
  };

  const closeDetailModal = () => {
    setDetailRow(null);
    setDetailRecord(null);
    setDetailLoading(false);
  };

  const openDetailModal = async (row) => {
    setDetailRow(row);
    setDetailRecord(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${apiBase}/cdr`, {
        params: { cdr_ids: row.cdr_id, limit: 1, page: 1 },
        timeout: 30000,
      });
      const rows = Array.isArray(res.data) ? res.data : (res.data?.records || []);
      setDetailRecord(rows[0] || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Çağrı kaydı yüklenemedi");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenInCdr = () => {
    if (!detailRow?.cdr_id || typeof onOpenCdrRecord !== "function") return;
    onOpenCdrRecord(detailRow.cdr_id, `Kayıt #${detailRow.cdr_id}`);
    closeDetailModal();
  };

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const pageIds = useMemo(() => pagination.paginatedItems.map((row) => row.id), [pagination.paginatedItems]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));
  const somePageSelected = pageIds.some((id) => selectedSet.has(id));

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const toggleSelectAll = () => {
    if (items.length > 0 && selectedIds.length === items.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(items.map((row) => row.id));
  };

  const pendingDeleteSet = useMemo(() => new Set(pendingDeleteIds), [pendingDeleteIds]);
  const pendingDeleteItems = useMemo(
    () => items.filter((item) => pendingDeleteSet.has(item.id)),
    [items, pendingDeleteSet],
  );
  const isDeleteBusy = bulkDeleting || deletingId !== null;

  const requestDeleteReports = (ids) => {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    if (uniqueIds.length === 0) return;
    setPendingDeleteIds(uniqueIds);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleteBusy) return;
    setDeleteConfirmOpen(false);
    setPendingDeleteIds([]);
  };

  const confirmDeleteReports = async () => {
    const uniqueIds = Array.from(new Set(pendingDeleteIds.filter(Boolean)));
    if (uniqueIds.length === 0) return;

    if (uniqueIds.length === 1) setDeletingId(uniqueIds[0]);
    else setBulkDeleting(true);

    try {
      await axios.delete(`${apiBase}/recording-error-reports`, {
        data: uniqueIds.length === 1 ? { id: uniqueIds[0] } : { ids: uniqueIds },
      });
      setItems((prev) => prev.filter((item) => !uniqueIds.includes(item.id)));
      setSelectedIds((prev) => prev.filter((id) => !uniqueIds.includes(id)));
      setNoteDrafts((prev) => {
        const next = { ...prev };
        uniqueIds.forEach((id) => { delete next[id]; });
        return next;
      });
      toast.success(uniqueIds.length === 1 ? "Bildirim silindi" : `${uniqueIds.length} bildirim silindi`);
      setDeleteConfirmOpen(false);
      setPendingDeleteIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bildirim silinemedi");
    } finally {
      setDeletingId(null);
      setBulkDeleting(false);
    }
  };

  const deleteReport = (row) => {
    requestDeleteReports([row.id]);
  };

  const deleteSelectedReports = () => {
    requestDeleteReports(selectedIds);
  };

  const applyRecheckResults = (updatedItems = [], fixedIds = []) => {
    if (!fixedIds.length && !updatedItems.length) return;
    const byId = new Map((updatedItems || []).map((item) => [item.id, item]));
    setItems((prev) => prev.map((item) => {
      if (!fixedIds.includes(item.id)) return item;
      return byId.get(item.id) ? { ...item, ...byId.get(item.id) } : { ...item, status: "fixed" };
    }));
  };

  const postRecheckIds = async (ids) => {
    const res = await axios.post(`${apiBase}/recording-error-reports`, {
      action: "recheck",
      ids,
    }, { timeout: RECHECK_ITEM_TIMEOUT_MS });
    return res.data;
  };

  const summarizeRecheckBatch = (totals, { single = false } = {}) => {
    const { fixedCount, stillUnavailable, noUrl, skippedNonPending, errors } = totals;
    if (single) {
      if (fixedCount > 0) {
        toast.success("Ses dosyası erişilebilir — düzeltildi olarak işaretlendi");
        return;
      }
      if (stillUnavailable > 0) toast.message("Ses hâlâ erişilemiyor", { description: "Manuel inceleyebilir veya düzeltildi işaretleyebilirsiniz." });
      else if (noUrl > 0) toast.warning("Ses URL bulunamadı");
      else if (skippedNonPending > 0) toast.info("Kayıt zaten sonuçlandırılmış");
      return;
    }
    if (fixedCount > 0) {
      toast.success(`${fixedCount} bildirim düzeltildi olarak güncellendi`);
    }
    const parts = [];
    if (stillUnavailable > 0) parts.push(`${stillUnavailable} erişilemiyor`);
    if (noUrl > 0) parts.push(`${noUrl} URL yok`);
    if (skippedNonPending > 0) parts.push(`${skippedNonPending} zaten kapalı`);
    if (errors > 0) parts.push(`${errors} kontrol hatası`);
    if (parts.length > 0) {
      toast.message(`Sayfa kontrolü bitti · ${parts.join(" · ")}`, {
        description: fixedCount > 0 ? "Kalan kayıtları manuel inceleyebilirsiniz." : undefined,
      });
    } else if (fixedCount === 0) {
      toast.info("Sayfa kontrolü tamamlandı; düzeltilecek kayıt yok");
    }
  };

  const runRecordingRecheckSequential = async (ids) => {
    const uniqueIds = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniqueIds.length === 0) {
      toast.warning("Bu sayfada kontrol edilecek kayıt yok");
      return;
    }
    if (uniqueIds.length === 1) {
      setRecheckingId(uniqueIds[0]);
      try {
        const data = await postRecheckIds(uniqueIds);
        applyRecheckResults(data?.items || [], data?.fixed_ids || []);
        summarizeRecheckBatch({
          fixedCount: data?.fixed_count ?? 0,
          stillUnavailable: data?.still_unavailable ?? 0,
          noUrl: data?.no_url ?? 0,
          skippedNonPending: data?.skipped_non_pending ?? 0,
          errors: 0,
        }, { single: true });
      } catch (error) {
        toast.error(error.response?.data?.detail || "Ses kontrolü yapılamadı");
      } finally {
        setRecheckingId(null);
      }
      return;
    }

    setRechecking(true);
    setRecheckProgress({ current: 0, total: uniqueIds.length });
    const totals = {
      fixedCount: 0,
      stillUnavailable: 0,
      noUrl: 0,
      skippedNonPending: 0,
      errors: 0,
    };

    try {
      for (let i = 0; i < uniqueIds.length; i += 1) {
        const id = uniqueIds[i];
        setRecheckProgress({ current: i + 1, total: uniqueIds.length });
        setRecheckingId(id);
        try {
          const data = await postRecheckIds([id]);
          totals.fixedCount += data?.fixed_count ?? 0;
          totals.stillUnavailable += data?.still_unavailable ?? 0;
          totals.noUrl += data?.no_url ?? 0;
          totals.skippedNonPending += data?.skipped_non_pending ?? 0;
          applyRecheckResults(data?.items || [], data?.fixed_ids || []);
        } catch {
          totals.errors += 1;
        }
      }
      summarizeRecheckBatch(totals);
    } finally {
      setRechecking(false);
      setRecheckingId(null);
      setRecheckProgress(null);
    }
  };

  const recheckCurrentPageReports = () => {
    const pageIds = pagination.paginatedItems.map((row) => row.id);
    runRecordingRecheckSequential(pageIds);
  };

  const recheckSingleReport = (row) => {
    runRecordingRecheckSequential([row.id]);
  };

  const updateReportStatus = async (row, status) => {
    setUpdatingId(row.id);
    try {
      const res = await axios.put(`${apiBase}/recording-error-reports`, {
        id: row.id,
        status,
        resolution_note: noteDrafts[row.id]?.trim() || null,
      });
      const updated = res.data?.item;
      if (updated) {
        setItems((prev) => prev.map((item) => (item.id === row.id ? { ...item, ...updated } : item)));
      } else {
        await fetchReports();
      }
      toast.success(
        status === "fixed" ? "Düzeltildi olarak işaretlendi"
          : status === "not_found" ? "Bulunamadı olarak işaretlendi"
            : "Tekrar incelemeye alındı"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Durum güncellenemedi");
    } finally {
      setUpdatingId(null);
    }
  };

  const getResolverTitle = (row) => {
    if (getReportStatus(row) === "pending") return "";
    const parts = [
      row.resolved_by_name,
      row.resolved_by_username ? `@${row.resolved_by_username}` : null,
      row.resolved_at ? formatDate(row.resolved_at) : null,
      row.resolution_note ? `Not: ${row.resolution_note}` : null,
    ].filter(Boolean);
    return parts.join(" · ");
  };

  const iconBtnClass = `h-7 w-7 p-0 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`;

  return (
    <PanelTableCard className={cardClass}>
      <PanelTableHeader
        title="Ses Kaydı Hata Bildirimleri"
        description="Bozuk veya kaybolan ses kayıtlarını inceleyin ve sonuçlandırın"
        icon={AlertTriangle}
        accent="amber"
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        toolbar={(
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || rechecking || pagination.paginatedItems.length === 0}
              onClick={recheckCurrentPageReports}
              className={`h-8 text-xs ${isDark ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 bg-emerald-500/5" : "border-emerald-400 text-emerald-800 hover:bg-emerald-50 bg-emerald-50/80"}`}
              title="Aktif sayfadaki kayıtları sırayla kontrol eder; erişilebilir sesler düzeltildi olur"
            >
              {rechecking ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  {recheckProgress ? `Kontrol ${recheckProgress.current}/${recheckProgress.total}` : "Kontrol…"}
                </>
              ) : (
                <>
                  <ScanSearch className="w-3.5 h-3.5 mr-1.5" />
                  Bu sayfayı kontrol et
                </>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchReports} disabled={loading} className={isDark ? "border-slate-700" : ""} title="Listeyi yenile">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        )}
      />

      <div className="px-4 pb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${labelClass}`} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ara..."
            className={`pl-8 h-8 text-xs ${inputClass}`}
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {STATUS_FILTERS.map((opt) => (
            <Button
              key={opt.id}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setStatusFilter(opt.id)}
              className={`h-7 px-2 text-[11px] ${pillClass(statusFilter === opt.id)}`}
            >
              {opt.label}
            </Button>
          ))}
          <span className={`mx-1 hidden sm:inline ${labelClass}`}>|</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`h-7 rounded-md border px-2 text-[11px] ${inputClass}`}
          >
            {TYPE_FILTERS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            disabled={loading || rechecking || pagination.paginatedItems.length === 0}
            onClick={recheckCurrentPageReports}
            className={`h-7 text-[11px] shrink-0 ${isDark ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
          >
            {rechecking ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                {recheckProgress ? `${recheckProgress.current}/${recheckProgress.total}` : "…"}
              </>
            ) : (
              <>
                <ScanSearch className="w-3 h-3 mr-1" />
                Bu sayfayı kontrol et
              </>
            )}
          </Button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className={`mx-4 mb-3 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${isDark ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-800"}`}>
          <span>{selectedIds.length} seçili</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={bulkDeleting || deletingId !== null}
            onClick={deleteSelectedReports}
            className={`h-7 text-xs ${isDark ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-rose-300 text-rose-700 hover:bg-rose-100"}`}
          >
            {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
            Seçilenleri Sil
          </Button>
          {selectedIds.length < items.length && items.length > 0 && (
            <Button type="button" size="sm" variant="ghost" onClick={toggleSelectAll} className={`h-7 text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>
              Tümünü seç ({items.length})
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={() => setSelectedIds([])} className={`h-7 text-xs ${isDark ? "text-slate-300" : "text-gray-600"}`}>
            Seçimi temizle
          </Button>
        </div>
      )}

      <PanelTableFrame isDark={isDark}>
        <PanelTableHeadRow isDark={isDark}>
          <PanelTableHead isDark={isDark} className="w-10">
            <Checkbox
              checked={allPageSelected ? true : (somePageSelected ? "indeterminate" : false)}
              onCheckedChange={toggleSelectPage}
              aria-label="Sayfadaki kayıtları seç"
            />
          </PanelTableHead>
          <PanelTableHead isDark={isDark}>Durum</PanelTableHead>
          <PanelTableHead isDark={isDark}>Kayıt</PanelTableHead>
          <PanelTableHead isDark={isDark}>Ses</PanelTableHead>
          <PanelTableHead isDark={isDark}>Hata</PanelTableHead>
          <PanelTableHead isDark={isDark}>Bildiren</PanelTableHead>
          <PanelTableHead isDark={isDark}>Numara</PanelTableHead>
          <PanelTableHead isDark={isDark}>Agent</PanelTableHead>
          <PanelTableHead isDark={isDark}>İşlem</PanelTableHead>
          <PanelTableHead isDark={isDark} align="right">Aksiyon</PanelTableHead>
        </PanelTableHeadRow>
        <PanelTableBody>
          {loading ? (
            <PanelTableRow isDark={isDark}>
              <PanelTableCell colSpan={10} isDark={isDark} className="text-center py-16">
                <Loader2 className={`w-8 h-8 mx-auto animate-spin ${isDark ? "text-slate-400" : "text-gray-400"}`} />
              </PanelTableCell>
            </PanelTableRow>
          ) : pagination.paginatedItems.length === 0 ? (
            <PanelTableEmpty
              colSpan={10}
              isDark={isDark}
              icon={AlertTriangle}
              title="Kayıt yok"
              description="Seçilen filtreye uygun bildirim bulunamadı."
            />
          ) : pagination.paginatedItems.map((row) => {
            const status = getReportStatus(row);
            const isUpdating = updatingId === row.id;
            const isRechecking = recheckingId === row.id;
            const isDeleting = deletingId === row.id;
            const isBusy = isUpdating || isRechecking || isDeleting || bulkDeleting || rechecking;
            const resolverTitle = getResolverTitle(row);
            return (
              <PanelTableRow key={row.id} isDark={isDark}>
                <PanelTableCell isDark={isDark} className="w-10">
                  <Checkbox
                    checked={selectedSet.has(row.id)}
                    onCheckedChange={() => toggleSelectRow(row.id)}
                    aria-label={`Kayıt #${row.cdr_id} seç`}
                  />
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>{getStatusBadge(status)}</PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  <button
                    type="button"
                    onClick={() => openDetailModal(row)}
                    className={`font-mono text-xs font-semibold text-cyan-500 hover:text-cyan-400 hover:underline text-left ${isDark ? "focus-visible:ring-cyan-500/40" : "focus-visible:ring-cyan-600/40"} rounded outline-none focus-visible:ring-2`}
                    title="Çağrı detayını aç"
                  >
                    #{row.cdr_id}
                  </button>
                  <div className={`text-[10px] font-mono ${labelClass}`}>{formatDate(row.call_tarih, true)}</div>
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!row.recording_url}
                        onClick={() => handlePlayRecording(row)}
                        className={`h-7 w-7 p-0 ${row.recording_url ? (isDark ? "text-cyan-400 hover:text-cyan-300" : "text-blue-600 hover:text-blue-700") : labelClass}`}
                      >
                        <Volume2 className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{row.recording_url ? "Ses kaydını dinle" : "Ses URL yok"}</TooltipContent>
                  </Tooltip>
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  <span className={`text-[11px] ${getErrorTypeClass(row.error_type, isDark)}`}>
                    {getErrorTypeLabel(row.error_type)}
                  </span>
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  <div className={`text-[11px] truncate max-w-[100px] ${textClass}`} title={row.reported_by_name || ""}>
                    {row.reported_by_name || "—"}
                  </div>
                  <div className={`text-[10px] font-mono ${labelClass}`}>{formatDate(row.created_at, true)}</div>
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  <div className="text-[11px] font-mono truncate max-w-[110px]" title={row.arayan_numara || ""}>
                    {row.arayan_numara || "—"}
                  </div>
                  <div className={`text-[10px] font-mono truncate max-w-[110px] ${labelClass}`} title={row.aranan_numara || ""}>
                    {row.aranan_numara || "—"}
                  </div>
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  <span className={`text-[11px] truncate max-w-[90px] block ${textClass}`} title={row.agent_name || ""}>
                    {row.agent_name || "—"}
                  </span>
                </PanelTableCell>
                <PanelTableCell isDark={isDark}>
                  {status === "pending" ? (
                    <span className={`text-[11px] ${labelClass}`}>—</span>
                  ) : (
                    <span className={`text-[11px] truncate max-w-[80px] block ${textClass}`} title={resolverTitle}>
                      {row.resolved_by_name || "—"}
                    </span>
                  )}
                </PanelTableCell>
                <PanelTableCell isDark={isDark} align="right">
                  <PanelTableActions>
                    {status === "pending" ? (
                      <>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button type="button" variant="outline" size="sm" className={iconBtnClass} title="Not ekle">
                              <MessageSquare className="w-3.5 h-3.5" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className={`w-56 p-2 ${isDark ? "bg-slate-900 border-slate-800" : ""}`}>
                            <Input
                              value={noteDrafts[row.id] || ""}
                              onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                              placeholder="Not (opsiyonel)"
                              className={`h-8 text-xs ${inputClass}`}
                            />
                          </PopoverContent>
                        </Popover>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isBusy}
                              onClick={() => recheckSingleReport(row)}
                              className={`${iconBtnClass} ${isDark ? "text-cyan-400 border-cyan-500/40" : "text-cyan-700 border-cyan-300"}`}
                            >
                              {isRechecking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanSearch className="w-3.5 h-3.5" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Sesi kontrol et (erişilebilirse düzeltildi)</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isBusy}
                              onClick={() => updateReportStatus(row, "fixed")}
                              className={`${iconBtnClass} ${isDark ? "text-emerald-400 border-emerald-500/40" : "text-emerald-700 border-emerald-300"}`}
                            >
                              {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Manuel: düzeltildi işaretle</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isBusy}
                              onClick={() => updateReportStatus(row, "not_found")}
                              className={`${iconBtnClass} ${isDark ? "text-rose-400 border-rose-500/40" : "text-rose-700 border-rose-300"}`}
                            >
                              {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Bulunamadı</TooltipContent>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isBusy}
                            onClick={() => updateReportStatus(row, "pending")}
                            className={iconBtnClass}
                          >
                            {isUpdating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Tekrar aç</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isBusy}
                          onClick={() => deleteReport(row)}
                          className={`${iconBtnClass} ${isDark ? "text-rose-400 border-rose-500/30 hover:bg-rose-500/10" : "text-rose-600 border-rose-200 hover:bg-rose-50"}`}
                        >
                          {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Sil</TooltipContent>
                    </Tooltip>
                  </PanelTableActions>
                </PanelTableCell>
              </PanelTableRow>
            );
          })}
        </PanelTableBody>
      </PanelTableFrame>

      {!loading && items.length > 0 && (
        <PanelTableFooter isDark={isDark}>
          <PanelTablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            totalPages={pagination.totalPages}
            rangeStart={pagination.rangeStart}
            rangeEnd={pagination.rangeEnd}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
            pageSizeOptions={[20, 50, 100]}
            isDark={isDark}
          />
        </PanelTableFooter>
      )}

      {detailRow && (
        <PanelModal open onClose={closeDetailModal}>
          <PanelModalContent
            className={`w-full max-w-lg flex flex-col rounded-xl shadow-2xl overflow-hidden ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white border border-gray-200"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`shrink-0 flex items-start justify-between gap-3 px-4 py-3 border-b ${isDark ? "border-slate-800 bg-slate-900/80" : "border-gray-200 bg-gray-50"}`}>
              <div className="min-w-0">
                <h3 className={`text-base font-semibold ${textClass}`}>
                  Çağrı Kaydı <span className="font-mono text-cyan-500">#{detailRow.cdr_id}</span>
                </h3>
                <p className={`text-xs mt-0.5 ${labelClass}`}>Ses kaydı hata bildirimi detayı</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={closeDetailModal}
                className={`h-8 w-8 p-0 shrink-0 ${isDark ? "text-slate-400 hover:text-red-400 hover:bg-slate-800" : "hover:bg-red-50 hover:text-red-600"}`}
                title="Kapat"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {detailLoading ? (
                <div className={`flex items-center justify-center py-10 ${labelClass}`}>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Çağrı kaydı yükleniyor...
                </div>
              ) : (
                <>
                  <div className={`rounded-lg border p-3 grid grid-cols-2 gap-3 ${isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-200 bg-gray-50/80"}`}>
                    <DetailField label="Çağrı tarihi" value={formatDate(detailRecord?.tarih || detailRow.call_tarih)} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                    <DetailField label="Yön" value={formatCallDirection(detailRecord?.yon ?? detailRow.call_yon ?? detailRow.yon)} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                    <DetailField label="Arayan" value={detailRecord?.arayan_numara || detailRow.arayan_numara} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                    <DetailField label="Aranan" value={detailRecord?.aranan_numara || detailRow.aranan_numara} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                    <DetailField label="Konuşma süresi" value={formatDuration(detailRecord?.konusma_suresi ?? detailRow.konusma_suresi)} isDark={isDark} labelClass={labelClass} textClass={textClass} mono />
                    <DetailField label="Agent" value={detailRecord?.agent_name || detailRow.agent_name} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                    <DetailField label="Firma" value={detailRecord?.company_name || detailRow.company_name} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                    <DetailField label="CDR durumu" value={detailRecord?.durum_aciklama || detailRecord?.durum || detailRow.call_durum} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                  </div>

                  <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-amber-500/20 bg-amber-500/5" : "border-amber-200 bg-amber-50/60"}`}>
                    <p className={`text-xs font-semibold ${isDark ? "text-amber-300" : "text-amber-800"}`}>Hata bildirimi</p>
                    <div className="grid grid-cols-2 gap-3">
                      <DetailField label="Hata tipi" value={getErrorTypeLabel(detailRow.error_type)} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                      <DetailField label="Durum" value={STATUS_LABELS[getReportStatus(detailRow)] || getReportStatus(detailRow)} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                      <DetailField label="Bildiren" value={detailRow.reported_by_name} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                      <DetailField label="Bildirim tarihi" value={formatDate(detailRow.created_at)} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                      {detailRow.resolved_by_name && (
                        <DetailField label="İşlem yapan" value={detailRow.resolved_by_name} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                      )}
                      {detailRow.resolution_note && (
                        <div className="col-span-2">
                          <DetailField label="Çözüm notu" value={detailRow.resolution_note} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                        </div>
                      )}
                    </div>
                  </div>

                  {!detailRecord && !detailLoading && (
                    <p className={`text-xs ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                      CDR kaydı bulunamadı; tablodaki bildirim bilgileri gösteriliyor.
                    </p>
                  )}
                </>
              )}
            </div>

            <div className={`shrink-0 flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t ${isDark ? "border-slate-800 bg-slate-900/80" : "border-gray-200 bg-gray-50"}`}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!(detailRecord?.recording || detailRow.recording_url)}
                onClick={() => handlePlayRecording(detailRow, detailRecord)}
                className={isDark ? "border-slate-700 text-slate-200" : ""}
              >
                <Volume2 className="w-4 h-4 mr-1.5" />
                Ses kaydını dinle
              </Button>
              {typeof onOpenCdrRecord === "function" && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleOpenInCdr}
                  className={isDark ? "bg-cyan-600 hover:bg-cyan-500 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}
                >
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  CDR sekmesinde aç
                </Button>
              )}
            </div>
          </PanelModalContent>
        </PanelModal>
      )}

      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open) closeDeleteConfirm();
          else setDeleteConfirmOpen(true);
        }}
      >
        <AlertDialogContent className={isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? "text-white" : ""}>
              {pendingDeleteIds.length === 1 ? "Bildirimi sil" : "Seçili bildirimleri sil"}
            </AlertDialogTitle>
            <AlertDialogDescription className={isDark ? "text-slate-400" : ""}>
              {pendingDeleteIds.length === 1
                ? "Bu hata bildirimi kalıcı olarak silinecek. Bu işlem geri alınamaz."
                : `${pendingDeleteIds.length} hata bildirimi kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
              {pendingDeleteItems.length > 0 && (
                <span className="block mt-3 space-y-1 text-xs font-mono">
                  {pendingDeleteItems.slice(0, 5).map((row) => (
                    <span key={row.id} className="block">
                      #{row.cdr_id} · {getErrorTypeLabel(row.error_type)}
                      {row.arayan_numara ? ` · ${row.arayan_numara}` : ""}
                    </span>
                  ))}
                  {pendingDeleteItems.length > 5 && (
                    <span className={`block ${isDark ? "text-slate-500" : "text-gray-500"}`}>
                      +{pendingDeleteItems.length - 5} kayıt daha
                    </span>
                  )}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isDeleteBusy}
              className={isDark ? "border-slate-700 text-slate-300" : ""}
            >
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleteBusy}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteReports();
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {isDeleteBusy ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Siliniyor...
                </>
              ) : (
                pendingDeleteIds.length === 1 ? "Sil" : `${pendingDeleteIds.length} bildirimi sil`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PanelTableCard>
  );
};

export default RecordingErrorReportsSettings;
